import { STATUS_VACANCIES } from 'common/constants'
import { GroupType } from 'features/Contacts/Network/GroupPage/types'
import type { SpecialtyOffer, VacancyOfferOrFavorType } from 'features/Conversations/types'
import type {
  ActionTypes, ProfileType, TrustedUserType, UserBriefInfo, VacancyType
} from 'features/MyProfile/types'

const initialState = {
  profile: null as ProfileType | null,
  isActiveFcm: false,
  isRegistration: null as Boolean | null,
  loaders: [] as string[],
  progressLoadingFile: null as number | null,
  vacancies: [] as VacancyType[],
  isVacanciesLoading: true as boolean,
  specialities: {} as any,
  favoredVacancies: [] as VacancyOfferOrFavorType[],
  favoredSpecialties: [] as SpecialtyOffer[],
  offeredVacancies: [] as VacancyOfferOrFavorType[],
  isFullTrustContacts: false,
  isOpenContactsModal: false,
  trustUsers: {} as { [key: string]: TrustedUserType },
  commonTrustedContactList: {} as { [uid: string]: string[] },
  isGroupsLoading: true as boolean,
  groups: [] as GroupType[],
  groupToJoin: null as GroupType | null,
  isOpenAddUserToGroupModal: false,
  isOpenGroupInviteOptionsModal: false,
  isTrustOrUntrustLoading: false as boolean,
  isEditTrustContacts: false as boolean,
  isBlockingInProgress: false as boolean,
  uidToBeTrusted: '' as string,
  blockedUsers: {} as { [key: string]: UserBriefInfo }
}

export const ProfileReducer = (state = initialState, action: ActionTypes): typeof initialState => {
  switch (action.type) {
    case 'PROFILE__SET_MY_PROFILE': {
      const contacts = action.profile?.contacts && Array.isArray(action.profile?.contacts)
        ? action.profile.contacts
        : Object.keys(action.profile?.contacts || {})

      return {
        ...state,
        isRegistration: !action.profile,
        profile: action.profile ? {
          ...action.profile,
          contacts: action.profile?.contacts ? contacts : []
        } : null
      }
    }
    case 'PROFILE__ADD_CONTACT': {
      return {
        ...state,
        profile: {
          ...state.profile,
          contacts: [...(state.profile?.contacts || []), action.contactUid]
        } as ProfileType
      }
    }
    case 'PROFILE__ADD_CONTACTS': {
      return {
        ...state,
        profile: {
          ...state.profile,
          contacts: [...(state.profile?.contacts || []), ...action.contactUids]
        } as ProfileType
      }
    }
    case 'PROFILE__SET_MY_PROFILE_TRUST': {
      return {
        ...state,
        profile: { ...state.profile, trust: action.trustedUids } as ProfileType
      }
    }
    case 'PROFILE__SET_MY_PROFILE_BLOCKED': {
      return {
        ...state,
        profile: { ...state.profile, blackList: action.blockedUids } as ProfileType
      }
    }
    case 'PROFILE__SET_MY_PROFILE_BLOCKED_BY': {
      return {
        ...state,
        profile: { ...state.profile, blockedBy: action.blockedByUids } as ProfileType
      }
    }
    case 'PROFILE__SET_MY_PROFILE_BLOCKED_USERS': {
      return {
        ...state,
        blockedUsers: action.blockedUsers
      }
    }
    case 'PROFILE__SET_MY_PROFILE_IS_FULL_TRUST_CONTACTS': {
      return {
        ...state,
        profile: { ...state.profile, isFullTrustContacts: action.isFullTrustContacts } as ProfileType,
        uidToBeTrusted: action.uidToBeTrusted ?? ''
      }
    }
    case 'PROFILE__SET_MY_PROFILE_EDIT_TRUST_CONTACTS': {
      return {
        ...state,
        profile: { ...state.profile, isEditTrustContacts: action.isEditTrustContacts } as ProfileType
      }
    }
    case 'PROFILE__SET_MY_PROFILE_IS_OPEN_CONTACTS_MODAL': {
      return {
        ...state,
        isOpenContactsModal: action.isOpenContactsModal
      }
    }
    case 'PROFILE__SET_MY_PROFILE_IS_OPEN_ADD_USER_TO_GROUP_MODAL': {
      return {
        ...state,
        isOpenAddUserToGroupModal: action.isOpenAddUserToGroupModal
      }
    }
    case 'PROFILE__SET_MY_PROFILE_IS_OPEN_GROUP_INVITE_OPTIONS_MODAL': {
      return {
        ...state,
        isOpenGroupInviteOptionsModal: action.isOpenGroupInviteOptionsModal
      }
    }
    case 'PROFILE__SET_TRUST_OR_UNTRUST_LOADING': {
      return {
        ...state,
        isTrustOrUntrustLoading: action.isLoading
      }
    }
    case 'PROFILE__DELETE_MY_CONTACT': {
      return {
        ...state,
        profile: {
          ...state.profile,
          contacts: state.profile?.contacts?.filter((contactUid) => contactUid !== action.payload)
        } as ProfileType
      }
    }
    case 'PROFILE__SET_IS_BLOCKING_IN_PROGRESS': {
      return {
        ...state,
        isBlockingInProgress: action.value
      }
    }
    case 'PROFILE__BLOCK_USER': {
      const isAlreadyAdded = !!state.blockedUsers[action.payload.uid]
      return {
        ...state,
        profile: {
          ...state.profile,
          blackList: [...(state.profile?.blackList || []), action.payload.uid]
        } as ProfileType,
        blockedUsers: isAlreadyAdded
          ? state.blockedUsers
          : {
            ...state.blockedUsers,
            [action.payload.uid]: action.payload.profile
          }
      }
    }
    case 'PROFILE__UNBLOCK_USER': {
      const { [action.payload.uid]: _, ...remainingBlockedUsers } = state.blockedUsers
      return {
        ...state,
        profile: {
          ...state.profile,
          blackList: (state.profile?.blackList || []).filter((uid) => uid !== action.payload.uid)
        } as ProfileType,
        blockedUsers: remainingBlockedUsers
      }
    }
    case 'PROFILE__UPDATE_MY_PROFILE_PHOTO':
      return {
        ...state,
        profile: {
          ...state.profile,
          photoURL: action.photoURL,
          photo: action.photo
        } as ProfileType
      }
    case 'PROFILE__TOGGLE_LOADER': {
      let newLoaders: string[] = []

      if (state.loaders.includes(action.loader)) {
        newLoaders = state.loaders.filter((loader) => loader !== action.loader)
      } else {
        newLoaders = [...state.loaders, action.loader]
      }

      return {
        ...state,
        loaders: newLoaders
      }
    }
    case 'PROFILE__SET_IS_ACTIVE_FCM':
      return {
        ...state,
        isActiveFcm: action.isActiveFcm
      }
    case 'PROFILE__UPDATE_MY_SLOTS': {
      if (!state.profile) return state
      let updatedSlots = { ...state.profile?.slots }

      if (action.payload.action === 'add') {
        if (updatedSlots) {
          if (typeof action.payload.slot !== 'string') {
            updatedSlots = {
              ...updatedSlots,
              ...action.payload.slot
            }
            return {
              ...state,
              profile: {
                ...state.profile,
                slots: updatedSlots
              }
            }
          }
        }
      }
      if (typeof action.payload.slot === 'string') {
        delete updatedSlots[action.payload.slot]
        return {
          ...state,
          profile: {
            ...state.profile,
            slots: updatedSlots
          }
        }
      }
      return state
    }
    case 'PROFILE__SET_MY_VACANCIES_LOADING': {
      return {
        ...state,
        isVacanciesLoading: action.isLoading
      }
    }
    case 'PROFILE__SET_MY_VACANCIES': {
      return {
        ...state,
        vacancies: action?.vacancies,
        isVacanciesLoading: false
      }
    }
    case 'PROFILE__SET_TRUST_USERS': {
      return {
        ...state,
        trustUsers: action.trustUsers
      }
    }
    case 'PROFILE__SET_COMMON_TRUSTED_CONTACTS': {
      return {
        ...state,
        commonTrustedContactList: action.commonTrustedContactList
      }
    }
    case 'PROFILE__SET_ALL_MY_FAVORS_FOR_VACANCIES': {
      return {
        ...state,
        favoredVacancies: action?.favors
      }
    }
    case 'PROFILE__SET_ALL_OFFERS_FOR_VACANCIES': {
      return {
        ...state,
        offeredVacancies: action?.offers
      }
    }
    case 'PROFILE__SET_MY_FAVOR_FOR_VACANCY': {
      return {
        ...state,
        favoredVacancies: [
          ...state.favoredVacancies,
          action?.favor
        ]
      }
    }
    case 'PROFILE__ADD_NEW_VACANCY': {
      const vacancyExists = state.vacancies.some((vacancy) => vacancy.id === action?.vacancy?.id)
      if (vacancyExists) {
        return state
      }
      return {
        ...state,
        vacancies: [...state.vacancies, action?.vacancy]
      }
    }
    case 'PROFILE__EDIT_VACANCY': {
      const updatedVacancies = state.vacancies.map((vacancy) => {
        if (vacancy.id === action?.vacancy.id) {
          return {
            ...action.vacancy,
            ts: vacancy.ts,
            updated: new Date().toISOString()
          }
        }
        return vacancy
      })
      return {
        ...state,
        vacancies: updatedVacancies
      }
    }
    case 'PROFILE__CLOSE_VACANCY': {
      const updatedVacancies = state.vacancies.map((vacancy) => {
        if (vacancy.id === action.vacancyId) {
          return { ...vacancy, status: STATUS_VACANCIES.CLOSED }
        }
        return vacancy
      })
      return {
        ...state,
        vacancies: updatedVacancies
      }
    }
    case 'PROFILE__CLOSE_SPECIALITY': {
      const { uid, specialityId } = action.payload
      const updatedSpecialities = state.specialities[uid].data.map((speciality: any) => {
        if (speciality.id === specialityId) {
          return { ...speciality, status: STATUS_VACANCIES.CLOSED }
        }
        return speciality
      })
      return {
        ...state,
        specialities: {
          ...state.specialities,
          [uid]: {
            ...state.specialities[uid],
            data: updatedSpecialities
          }
        }
      }
    }
    case 'PROFILE__DELETE_VACANCY': {
      const updatedVacancies = state.vacancies.filter((vacancy) => vacancy.id !== action.vacancyId)
      return {
        ...state,
        vacancies: updatedVacancies
      }
    }
    case 'PROFILE__SET_SPECIALITIES': {
      return {
        ...state,
        specialities: {
          ...state.specialities,
          [action.payload.uid]: {
            isLoaded: true,
            data: action.payload.specialities
          }
        }
      }
    }
    case 'PROFILE__ADD_NEW_SPECIALITY': {
      const specialityExists = state.specialities[action.payload.uid]?.data?.some(
        (speciality: any) => speciality.id === action.payload.speciality.id
      )
      if (specialityExists) {
        return state
      }
      return {
        ...state,
        specialities: {
          ...state.specialities,
          [action.payload.uid]: {
            isLoaded: true,
            data: [...(state.specialities[action.payload.uid] ?? {})?.data ?? [], action.payload.speciality]
          }
        }
      }
    }
    case 'PROFILE__DELETE_SPECIALITY': {
      return {
        ...state,
        specialities: {
          ...state.specialities,
          [action.payload.uid]: {
            isLoaded: true,
            data: state.specialities[action.payload.uid].data.filter((speciality: any) => {
              return speciality.id !== action.payload.specialityId
            })
          }
        }
      }
    }
    case 'PROFILE__EDIT_SPECIALITY': {
      const updatedSpecialities = state.specialities[action.payload.uid].data.map((speciality: any) => {
        if (speciality.id === action.payload.speciality.id) {
          return {
            ...action.payload.speciality,
            ts: speciality.ts,
            updated: new Date().toISOString()
          }
        }
        return speciality
      })
      return {
        ...state,
        specialities: {
          ...state.specialities,
          [action.payload.uid]: {
            isLoaded: true,
            data: updatedSpecialities
          }
        }
      }
    }
    case 'PROFILE__SET_ALL_MY_FAVORS_FOR_SPECIALTIES': {
      return {
        ...state,
        favoredSpecialties: action?.favors
      }
    }
    case 'PROFILE__ADD_MY_FAVOR_FOR_SPECIALTY': {
      return {
        ...state,
        favoredSpecialties: [
          ...state.favoredSpecialties,
          action?.favor
        ]
      }
    }
    case 'PROFILE__SET_MY_GROUPS': {
      const newGroups = action.groups.filter(
        (group) => !state.groups.some((existingGroup) => existingGroup.id === group.id)
      )
      return {
        ...state,
        isGroupsLoading: false,
        groups: [
          ...state.groups,
          ...newGroups.map((group) => ({
            ...group,
            isFullData: false
          }))
        ]
      }
    }
    case 'PROFILE__ADD_NEW_GROUP': {
      return {
        ...state,
        groups: state.groups.some((group) => group.id === action?.group?.id)
          ? state.groups.map((group) =>
            (group.id === action?.group?.id && !group.isFullData
              ? { ...group, ...action?.group }
              : group))
          : [...state.groups, action?.group]
      }
    }
    case 'PROFILE__UPDATE_GROUP': {
      const groups = state.groups.map((group) =>
        (group.id === action.payload.group.id
          ? {
            ...action.payload.group,
            isFullData: action.payload.isFullData ?? group.isFullData
          }
          : group))
      const shouldAdd = !state.groups.find((group) => group.id === action.payload.group.id)
      if (shouldAdd) {
        groups.push({
          ...action.payload.group,
          isFullData: true
        })
      }
      return {
        ...state,
        groups
      }
    }
    case 'PROFILE__UPDATE_GROUP_PRIVACY': {
      return {
        ...state,
        groups: state.groups.map((group) =>
          (group.id === action.payload.groupId
            ? {
              ...group,
              privacy: action.payload.privacy
            }
            : group))
      }
    }
    case 'PROFILE__REMOVE_USER_FROM_GROUP': {
      return {
        ...state,
        groups: state.groups.map((group) =>
          (group.id === action.payload.groupId
            ? {
              ...group,
              members: group.members.filter((member) => member !== action.payload.userId),
              members_data: group.members_data?.filter((member) => member.uid !== action.payload.userId) || []
            }
            : group))
      }
    }
    case 'PROFILE__ADD_USER_TO_GROUP': {
      return {
        ...state,
        groups: state.groups.map((group) =>
          (group.url === action.payload.groupRef
            ? {
              ...group,
              members: [...group.members, action.payload.userId],
              members_data: [...group.members_data || [], action.payload.userData],
              requests: group.requests?.filter((user) => user.uid !== action.payload.userId)
            }
            : group))
      }
    }
    case 'PROFILE__RESET_GROUP_REF': {
      return {
        ...state,
        groups: state.groups.map((group) =>
          (group.id === action.payload.groupId
            ? {
              ...group,
              url: action.payload.url
            }
            : group))
      }
    }
    case 'PROFILE__DENY_JOIN_REQUEST': {
      return {
        ...state,
        groups: state.groups.map((group) =>
          (group.id === action.payload.groupId
            ? {
              ...group,
              requests: group.requests?.filter((user) => user?.uid !== action.payload.uid)
            }
            : group))
      }
    }
    case 'PROFILE__APPROVE_JOIN_REQUEST': {
      return {
        ...state,
        groups: state.groups.map((group) =>
          (group.url === action.payload.groupRef
            ? {
              ...group,
              members: [...group.members, action.payload.uid],
              members_data: [
                ...group.members_data || [],
                ...(group.requests?.filter((user) => user.uid === action.payload.uid) || [])
              ],
              requests: group.requests?.filter((user) => user.uid !== action.payload.uid)
            }
            : group))
      }
    }
    case 'PROFILE__ADD_JOIN_REQUEST': {
      return {
        ...state,
        groups: state.groups.map((group) => {
          if (group.id === action.payload.groupId) {
            const isUserMember = group.members?.some((uid) => uid === action.payload.uid)
            if (!isUserMember) {
              const updatedRequests = [
                ...(group.requests || []).filter((user) => user?.uid !== action.payload.uid),
                action.payload.request
              ]
              return {
                ...group,
                requests: updatedRequests
              }
            }
          }
          return group
        })
      }
    }
    case 'PROFILE__DELETE_GROUP': {
      return {
        ...state,
        groups: state.groups.filter(
          (group) => group.id !== action.groupIdentifier && group.url !== action.groupIdentifier
        )
      }
    }
    case 'PROFILE__SET_MY_GROUPS_LOADING': {
      return {
        ...state,
        isGroupsLoading: action.isLoading
      }
    }
    case 'PROFILE__OPEN_GROUP_JOIN_MODAL': {
      return {
        ...state,
        groupToJoin: action.group
      }
    }
    case 'PROFILE__CLOSE_GROUP_JOIN_MODAL': {
      return {
        ...state,
        groupToJoin: state.groupToJoin?.id === action.groupId ? null : state.groupToJoin
      }
    }
    case 'PROFILE__UPDATE_GROUP_TO_JOIN_STATUS': {
      return {
        ...state,
        groupToJoin: state.groupToJoin?.id === action.payload.groupId
          ? {
            ...state.groupToJoin,
            request: { ...(state.groupToJoin.request || {}), status: action.payload.status }
          }
          : state.groupToJoin
      }
    }
    default: return state
  }
}
